/**
 * Shared low-level utilities for the spatial-filter family.
 *
 * Pulled out so every filter module can share the same mirror-extend
 * boundary logic and the planar/packed helpers without each duplicating
 * a personal copy. Keeps file sizes well below the 800-line cap and
 * makes the boundary semantics a single point of truth (zero risk of
 * one filter using clamp while another uses zero-pad).
 *
 * License: BLRRD is MIT. NO GPLv3 source (GIMP/GEGL/darktable/RT) was
 * consulted. The math here is textbook DSP.
 *
 * @module src/pipeline/filters/spatial/_util
 */

/**
 * Reflect an out-of-bounds index back into [0, n-1] using mirror-extend
 * (no boundary repetition — the edge sample is reflected, not doubled).
 *
 * Used as the canonical boundary handler for every spatial filter that
 * needs to sample outside the image bounds. Mirror-extend avoids the
 * zero-pad halo that would otherwise darken the edges of a blurred
 * image.
 *
 * Period of the reflected sequence is 2(n-1); a single modulo into that
 * period handles arbitrarily-far-out indices.
 *
 * @param {number} i  potentially out-of-bounds index
 * @param {number} n  size of the dimension
 * @returns {number}  index in [0, n-1]
 */
export function mirrorIndex(i, n) {
    if (n <= 1) return 0;
    const period = 2 * (n - 1);
    let m = i % period;
    if (m < 0) m += period;
    return m < n ? m : period - m;
}

/**
 * Extract a single colour channel from a packed RGBA Float32 buffer
 * into a planar single-channel Float32 buffer of length w*h.
 *
 * @param {Float32Array} rgba    packed RGBA, length 4*w*h
 * @param {number} channel       0=R, 1=G, 2=B, 3=A
 * @param {number} w
 * @param {number} h
 * @returns {Float32Array}       new buffer of length w*h
 */
export function planarFromRGBA(rgba, channel, w, h) {
    const n = w * h;
    const out = new Float32Array(n);
    for (let i = 0, j = channel; i < n; i++, j += 4) out[i] = rgba[j];
    return out;
}

/**
 * Write a planar single-channel buffer back into the corresponding
 * channel of a packed RGBA Float32 buffer.
 *
 * @param {Float32Array} planar  length w*h
 * @param {Float32Array} rgba    packed RGBA, length 4*w*h
 * @param {number} channel       0=R, 1=G, 2=B, 3=A
 * @param {number} w
 * @param {number} h
 */
export function rgbaFromPlanar(planar, rgba, channel, w, h) {
    const n = w * h;
    for (let i = 0, j = channel; i < n; i++, j += 4) rgba[j] = planar[i];
}

/**
 * Validate the common shape of a public filter entry point. Throws on
 * mismatch with a self-describing error message. Each filter's first
 * argument-check delegates here to avoid copy-pasted error strings.
 *
 * @param {string} fn            filter function name (for error msgs)
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 */
export function validateRGBA(fn, rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new Error(`${fn}: rgba must be Float32Array`);
    }
    const expected = w * h * 4;
    if (rgba.length !== expected) {
        throw new Error(`${fn}: length ${rgba.length} != 4·${w}·${h} (${expected})`);
    }
    if (w <= 0 || h <= 0) {
        throw new Error(`${fn}: w and h must be positive`);
    }
}

/**
 * Rec.709 luminance weights — applied to scene-linear ACEScg. Mild
 * primary mismatch vs. true AP1 weights but produces a valid achromatic
 * projection. Re-used here so threshold-driven filters (USM threshold,
 * Sobel magnitude, etc.) can share one definition.
 */
export const LUMA_R = 0.2126;
export const LUMA_G = 0.7152;
export const LUMA_B = 0.0722;

/**
 * Compute the Rec.709 luminance of one packed RGBA sample.
 *
 * @param {Float32Array} rgba
 * @param {number} idx           pixel index (NOT byte offset; multiplied
 *                               by 4 internally)
 * @returns {number}
 */
export function luma(rgba, idx) {
    const p = idx * 4;
    return LUMA_R * rgba[p] + LUMA_G * rgba[p + 1] + LUMA_B * rgba[p + 2];
}
